import React, { useEffect, useRef } from "react";
import {
  ShieldCheck,
  Users,
  BookOpen,
  Video,
  Bell,
  FileText,
  CheckCircle,
  Database,
  PieChart,
  ClipboardCheck,
  GraduationCap,
  Sparkles,
  Zap,
  Globe
} from "lucide-react";
import SEO from "../components/SEO";
import "./EdVedhaPage.css";

const EdVedhaPage = () => {
  const observerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-fancy-in-view');
        }
      });
    }, { threshold: 0.1 });

    const hiddenElements = document.querySelectorAll('.fancy-reveal-scroll');
    hiddenElements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 12;
    const rotateY = (centerX - x) / 12;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;

    // Gloss shimmer
    const shimmer = card.querySelector('.fancy-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,255,255,0.4) 0%, transparent 80%)`;
    }
  };

  const handleMouseLeave = (index) => {
    const card = cardRefs.current[index];
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    const shimmer = card.querySelector('.fancy-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `transparent`;
    }
  };

  const features = [
    {
      title: "Authentication Module",
      icon: <ShieldCheck size={32} />,
      img: "/images/about-001.png",
      badge: "Security First",
      points: [
        "Secure login for ERP staff, students, instructors",
        "JWT-based secure sessions",
        "Role-based dashboard (Admin, Instructor, Student)"
      ]
    },
    {
      title: "User Management",
      icon: <Users size={32} />,
      img: "/images/about-002.png",
      badge: "Collaborative",
      points: [
        "Define user roles and restrictions",
        "Monitor academic progress and submissions",
        "Manage content access for students and faculty"
      ]
    },
    {
      title: "Course Management",
      icon: <BookOpen size={32} />,
      img: "/images/about-004.png",
      badge: "Dynamic",
      points: [
        "Add/edit courses",
        "Upload content: videos, PDFs, MCQs",
        "Assign instructors & map courses to batches"
      ]
    },
    {
      title: "Learning Content",
      icon: <GraduationCap size={32} />,
      badge: "Anytime Access",
      points: [
        "Access courseware anywhere",
        "Track lesson progress",
        "Simulated case studies and gamified quizzes"
      ]
    },
    {
      title: "Notifications",
      icon: <Bell size={32} />,
      img: "/images/about-005.png",
      badge: "Real-time Alerts",
      points: [
        "Push alerts for assignments and exams",
        "Live class notifications",
        "Access restriction and deadline alerts"
      ]
    },
    {
      title: "Live Class Integration",
      icon: <Video size={32} />,
      img: "/images/about-003.png",
      badge: "Seamless Video",
      points: [
        "Schedule sessions by date/time/topic",
        "Google Meet / Zoom link sharing",
        "Display in student calendar"
      ]
    },
    {
      title: "Assessment & Exams",
      icon: <ClipboardCheck size={32} />,
      badge: "Smart Evaluation",
      points: [
        "Create MCQ and written exams with timers",
        "Auto/manual evaluation supported",
        "Export score reports"
      ]
    },
    {
      title: "Assignment Module",
      icon: <FileText size={32} />,
      badge: "Feedback Driven",
      points: [
        "Instructors upload instructions",
        "Students submit with attachments",
        "Grades + personalized feedback"
      ]
    },
    {
      title: "Question Bank",
      icon: <Database size={32} />,
      badge: "Scalable Library",
      points: [
        "Create and organize reusable questions",
        "Link to subjects/topics/exams",
        "Smart searchable library"
      ]
    },
    {
      title: "Reports & Dashboards",
      icon: <PieChart size={32} />,
      badge: "Insights",
      points: [
        "Track performance & attendance",
        "Monitor fee status",
        "Log user activity (logins, actions, etc.)"
      ]
    }
  ];

  return (
    <div className="premium-edvedha-wrapper">
      <SEO
        title="EdVedha — India's 3D AR Learning & LMS Platform | DBS"
        description="Transform education with DBS EdVedha: modern Learning Management System with automated learning paths, immersive 3D AR modules, live classes, and smart student tracking."
        keywords="EdVedha, EdVedha LMS, DBS EdVedha, 3D AR learning India, learning management system, online classroom software"
        canonical="https://dbasesolutions.in/edvedha"
        ogImage="/og-edvedha.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Products", url: "/products" },
          { name: "EdVedha LMS", url: "/edvedha" }
        ]}
      />
      {/* Immersive Galaxy Background */}
      <div className="galaxy-cosmos-bg">
        <div className="cosmic-nebula orb-alt-1"></div>
        <div className="cosmic-nebula orb-alt-2"></div>
        <div className="cosmic-nebula orb-alt-3"></div>
        <div className="star-field">
          {[...Array(30)].map((_, i) => (
            <div key={i} className={`star s${i}`}></div>
          ))}
        </div>
      </div>

      <div className="lms-content-root">
        <header className="lms-hero-banner fancy-reveal-scroll">
          <div className="hero-status-pill">
            <Zap size={14} className="zap-glow" />
            <span>Empowering Modern Education</span>
          </div>
          <div className="logo-box-3d">
            <img src="/images/EdVedha-Logo.png" alt="EdVedha Logo" className="floating-logo" />
          </div>
          <h1 className="lms-hero-title">
            Next-Gen <span className="shimmering-gradient">Learning</span> Mastery
          </h1>
          <p className="lms-hero-desc">
            DBS EdVedha is a future-ready LMS designed to support the entire academic lifecycle, streamlining user management and content delivery in one integrated digital ecosystem.
          </p>

          <div className="lms-hero-decorations">
            <GraduationCap className="deco-icon d1" />
            <Database className="deco-icon d2" />
            <PieChart className="deco-icon d3" />
          </div>
        </header>

        <section className="lms-overview-card fancy-reveal-scroll">
          <div className="overview-glass-inner">
            <div className="overview-top-line">
              <Globe size={24} className="spinning-globe" />
              <h2>Digital Transformation</h2>
            </div>
            <p>
              DBS EdVedha is a comprehensive Learning Management System designed to support the entire academic lifecycle of educational institutions. It streamlines user management, content delivery, assessments, and performance monitoring through an integrated digital platform.

Instructors benefit from tools to upload and manage digital content, create assignments, schedule assessments, and conduct live classes. Students can access learning materials, submit assignments, attend virtual sessions, and track their academic progress seamlessly.

The platform also supports fee-based access control, real-time notifications, and powerful dashboards. Built with Ionic React, .NET Core, and SQL Server — it is mobile-friendly, secure, and scalable.
            </p>
          </div>
        </section>

        <section className="lms-modules-container">
          <div className="lms-section-header fancy-reveal-scroll">
            <h2 className="lms-section-title">Core <span className="text-secondary-glow">Modules</span></h2>
            <div className="decorative-header-line"></div>
          </div>

          <div className="premium-features-grid">
            {features.map((feature, index) => (
              <div
                key={index}
                ref={el => cardRefs.current[index] = el}
                className="lms-module-card fancy-reveal-scroll"
                onMouseMove={(e) => handleMouseMove(e, index)}
                onMouseLeave={() => handleMouseLeave(index)}
              >
                <div className="fancy-card-shimmer"></div>
                <div className="lms-card-content">
                  <div className="lms-card-top">
                    <div className="lms-icon-box">
                      {feature.img ? (
                        <img src={feature.img} alt={feature.title} className="icon-img-lms" />
                      ) : (
                        feature.icon
                      )}
                    </div>
                    <span className="lms-mini-badge">{feature.badge}</span>
                  </div>
                  <h4 className="lms-card-heading">{feature.title}</h4>
                  <ul className="lms-p-list">
                    {feature.points.map((p, i) => (
                      <li key={i}><Sparkles size={12} className="s-icon" /> {p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="lms-highlights-fusion fancy-reveal-scroll">
          <div className="fusion-banner-inner">
            <div className="fusion-left-txt">
              <h2 className="fusion-title">Project Excellence</h2>
              <div className="fusion-list-modern">
                <div className="fusion-item">
                  <div className="chk-box"><CheckCircle size={18} /></div>
                  <p>Full academic lifecycle mastery</p>
                </div>
                <div className="fusion-item">
                  <div className="chk-box"><CheckCircle size={18} /></div>
                  <p>Integrated role-based dashboards</p>
                </div>
                <div className="fusion-item">
                  <div className="chk-box"><CheckCircle size={18} /></div>
                  <p>Live classes & real-time assessment</p>
                </div>
                <div className="fusion-item">
                  <div className="chk-box"><CheckCircle size={18} /></div>
                  <p>Secure, Scalable & 100% Mobile Ready</p>
                </div>
              </div>
            </div>
            <div className="fusion-right-gfx">
              <div className="gfx-orb-container">
                <img src="/images/about-003.png" alt="Excellence" className="lava-gfx" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EdVedhaPage;

